// src/lib/services/data/DataSyncService.ts
// Single entry point for all data state mutations from WebSocket messages.
// Handles DATA_SNAPSHOT, DATA_DELTA, and DATA_TICK events.

import { smartUpdateAlerts, smartUpdateWorkOrders, applyAlertDelta } from './smartUpdate';
import { domainState } from '$lib/state/index.svelte';
import { createLogger } from '$lib/utils/logger';

type DataHealthStatus = 'fresh' | 'stale' | 'empty_cache' | 'unavailable' | 'site_data_missing' | 'unknown';

const log = createLogger('DataSyncService');

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface BackendGroup {
	asset_id: string;
	asset_name: string;
	manager: string;
	alerts: Record<string, unknown>[];
	daily_data?: Record<string, unknown>[];
}

interface SnapshotPayload {
	regular_alerts?: BackendGroup[];
	approved_alerts?: BackendGroup[];
	work_orders?: unknown[];
	snapshot_ts?: string;
	[key: string]: unknown;
}

interface DeltaItem {
	[key: string]: unknown;
}

interface DiffEntry {
	added?: DeltaItem[];
	removed?: DeltaItem[];
	modified?: DeltaItem[];
}

interface TickDiff {
	alerts_diff?: DiffEntry;
	work_orders_diff?: DiffEntry;
}

// ---------------------------------------------------------------------------
// handleSnapshot
// ---------------------------------------------------------------------------

/**
 * Called when DATA_SNAPSHOT arrives.
 * Routes to alert or work-order update functions based on entity type.
 *
 * @param entity     - 'alerts' | 'work_orders' | 'all'
 * @param data       - Raw payload from the WebSocket message
 * @param dataStatus - Health status from backend ('fresh' | 'stale' | 'empty_cache' | 'unavailable' | 'unknown')
 */
function handleSnapshot(entity: string, data: SnapshotPayload, dataStatus: DataHealthStatus = 'fresh'): void {
	const t0 = performance.now();

	if (entity === 'alerts') {
		const groupedRegular = data.regular_alerts ?? [];
		const groupedApproved = data.approved_alerts ?? [];
		const snapshotTs = data.snapshot_ts;
		smartUpdateAlerts(groupedRegular, groupedApproved, snapshotTs);
		domainState.alerts.dataHealth = dataStatus;
	} else if (entity === 'work_orders') {
		const items = Array.isArray(data) ? (data as unknown[]) : (data.work_orders ?? []);
		smartUpdateWorkOrders(items);
	} else if (entity === 'all') {
		const groupedRegular = data.regular_alerts ?? [];
		const groupedApproved = data.approved_alerts ?? [];
		const snapshotTs = data.snapshot_ts;
		smartUpdateAlerts(groupedRegular, groupedApproved, snapshotTs);
		domainState.alerts.dataHealth = dataStatus;

		const workOrders = data.work_orders ?? [];
		smartUpdateWorkOrders(workOrders);
	} else if (entity === 'hardware_alerts') {
		const items = (data as any).items ?? [];
		domainState.hardwareAlerts.items = items;
		domainState.hardwareAlerts.isLoading = false;
		domainState.hardwareAlerts.initialized = true;
		domainState.hardwareAlerts.lastUpdateTime = new Date();
		log.info('Hardware alerts snapshot received', { count: items.length });
	} else {
		log.warn('handleSnapshot: unknown entity type — ignoring', { entity });
		return;
	}

	const elapsed = (performance.now() - t0).toFixed(1);
	log.debug('handleSnapshot complete', { entity, dataStatus, elapsed: `${elapsed}ms` });
}

// ---------------------------------------------------------------------------
// handleDelta
// ---------------------------------------------------------------------------

/**
 * Called when DATA_DELTA arrives (single item change).
 *
 * @param entity - 'alerts' | 'approved_alerts' | 'work_orders'
 * @param action - 'add' | 'update' | 'remove'
 * @param data   - The item payload (single object or array)
 */
function handleDelta(entity: string, action: string, data: DeltaItem | DeltaItem[]): void {
	if (entity === 'alerts' || entity === 'approved_alerts') {
		// data may be a single alert or an array of alerts
		const alerts: DeltaItem[] = Array.isArray(data) ? data : [data];
		for (const alert of alerts) {
			applyAlertDelta(action, alert as unknown as Parameters<typeof applyAlertDelta>[1]);
		}
	} else if (entity === 'work_orders') {
		const items = domainState.workOrders.items;

		if (action === 'remove') {
			const incoming = Array.isArray(data) ? data : [data];
			const removeIds = new Set(incoming.map((d) => d.id));
			domainState.workOrders.items = items.filter((wo: DeltaItem) => !removeIds.has(wo.id));
		} else if (action === 'add' || action === 'update') {
			const incoming = Array.isArray(data) ? data : [data];
			const updated = [...items];

			for (const newItem of incoming) {
				const idx = updated.findIndex((wo: DeltaItem) => wo.id === newItem.id);
				if (idx !== -1) {
					// Field-merge: preserve nested fields (e.g. alerts array on WO)
					updated[idx] = { ...(updated[idx] as DeltaItem), ...newItem };
				} else {
					updated.push(newItem);
				}
			}

			domainState.workOrders.items = updated;
		} else {
			log.warn('handleDelta: unknown action for work_orders', { action });
		}
	} else if (entity === 'hardware_alerts') {
		const items = domainState.hardwareAlerts.items;

		if (action === 'remove') {
			const incoming = Array.isArray(data) ? data : [data];
			const removeIds = new Set(incoming.map((d) => d.id));
			domainState.hardwareAlerts.items = items.filter((ha: DeltaItem) => !removeIds.has(ha.id));
		} else if (action === 'add' || action === 'update') {
			const incoming = Array.isArray(data) ? data : [data];
			const updated = [...items];

			for (const newItem of incoming) {
				const idx = updated.findIndex((ha: DeltaItem) => ha.id === newItem.id);
				if (idx !== -1) {
					updated[idx] = { ...(updated[idx] as DeltaItem), ...newItem };
				} else {
					updated.push(newItem);
				}
			}

			domainState.hardwareAlerts.items = updated;
		} else {
			log.warn('handleDelta: unknown action for hardware_alerts', { action });
		}
	} else {
		log.warn('handleDelta: unknown entity type — ignoring', { entity });
	}

	log.debug('handleDelta complete', { entity, action });
}

// ---------------------------------------------------------------------------
// handleTick
// ---------------------------------------------------------------------------

/**
 * Called when DATA_TICK arrives (periodic diff broadcast from backend).
 * Applies all changes in the diff, then rebuilds grouped arrays once at the end.
 *
 * @param diff - Object with optional alerts_diff and work_orders_diff
 */
function handleTick(diff: TickDiff): void {
	if (!diff) return;

	// --- Alerts diff ---
	if (diff.alerts_diff) {
		const alertsDiff = diff.alerts_diff;

		const toProcess: Array<{ action: string; items: DeltaItem[] }> = [];

		if (Array.isArray(alertsDiff.removed) && alertsDiff.removed.length > 0) {
			toProcess.push({ action: 'remove', items: alertsDiff.removed });
		}
		if (Array.isArray(alertsDiff.added) && alertsDiff.added.length > 0) {
			toProcess.push({ action: 'add', items: alertsDiff.added });
		}
		if (Array.isArray(alertsDiff.modified) && alertsDiff.modified.length > 0) {
			toProcess.push({ action: 'update', items: alertsDiff.modified });
		}

		for (const { action, items } of toProcess) {
			for (const alert of items) {
				applyAlertDelta(action, alert as unknown as Parameters<typeof applyAlertDelta>[1]);
			}
		}
	}

	// --- Work orders diff ---
	if (diff.work_orders_diff) {
		const woDiff = diff.work_orders_diff;
		let items = [...domainState.workOrders.items];
		let changed = false;

		if (Array.isArray(woDiff.removed) && woDiff.removed.length > 0) {
			const removeIds = new Set(woDiff.removed.map((d: DeltaItem) => d.id));
			items = items.filter((wo: DeltaItem) => !removeIds.has(wo.id));
			changed = true;
		}

		const toMerge: DeltaItem[] = [
			...(Array.isArray(woDiff.added) ? woDiff.added : []),
			...(Array.isArray(woDiff.modified) ? woDiff.modified : [])
		];

		for (const newItem of toMerge) {
			const idx = items.findIndex((wo: DeltaItem) => wo.id === newItem.id);
			if (idx !== -1) {
				items[idx] = { ...(items[idx] as DeltaItem), ...newItem };
			} else {
				items.push(newItem);
			}
			changed = true;
		}

		if (changed) {
			domainState.workOrders.items = items;
		}
	}

	log.debug('handleTick complete', {
		hasAlertsDiff: !!diff.alerts_diff,
		hasWoDiff: !!diff.work_orders_diff
	});
}

// ---------------------------------------------------------------------------
// clearAll
// ---------------------------------------------------------------------------

/**
 * Called on logout. Resets alerts and workOrders state to empty defaults.
 */
function clearAll(): void {
	domainState.alerts.items = [];
	domainState.alerts.approvedItems = [];
	domainState.alerts.groupedItems = [];
	domainState.alerts.groupedApprovedItems = [];
	domainState.alerts.financialData = [];
	domainState.alerts.isLoading = true;
	domainState.alerts.initialized = false;
	domainState.alerts.dataHealth = 'fresh';

	domainState.workOrders.items = [];
	domainState.workOrders.isLoading = true;
	domainState.workOrders.initialized = false;

	domainState.hardwareAlerts.items = [];
	domainState.hardwareAlerts.isLoading = true;
	domainState.hardwareAlerts.initialized = false;
	domainState.hardwareAlerts.lastUpdateTime = null;

	log.debug('clearAll: domain state reset');
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const dataSyncService = { handleSnapshot, handleDelta, handleTick, clearAll };
